/**
 * Pure utility functions shared between CompetitiveSessionPage and its tests.
 *
 * Extracted from CompetitiveSessionPage.js so the critical persistence-diff logic
 * (sameCourtLineup, shouldSkipCourtMerge, sessionStateChanged) can be unit-tested
 * independently of the full component tree.
 */

/**
 * Returns a stable, order-independent string key for a team array.
 * Used for lineup equality checks that are independent of player order.
 */
export function sortedTeamKey(team) {
  if (!team || team.length === 0) return '';
  return [...team].map((id) => String(id)).sort().join(',');
}

/**
 * True when two court objects have the same court number and the same players
 * on each team (order-independent). Used to detect roster changes that need
 * to be persisted.
 */
export function sameCourtLineup(a, b) {
  return (
    a.courtNumber === b.courtNumber &&
    sortedTeamKey(a.team1) === sortedTeamKey(b.team1) &&
    sortedTeamKey(a.team2) === sortedTeamKey(b.team2)
  );
}

/** Same players on each team (order-independent), ignoring courtNumber / slot index. */
export function sameRoster(a, b) {
  if (!a || !b) return false;
  return (
    sortedTeamKey(a.team1) === sortedTeamKey(b.team1) &&
    sortedTeamKey(a.team2) === sortedTeamKey(b.team2)
  );
}

/**
 * True when the fresh row from DB should NOT overwrite local state.
 *
 * Skip when:
 *   • fresh is strictly older (stale realtime event, out-of-order delivery)
 *   • fresh is the same version AND same lineup (no change — avoids no-op state update)
 */
export function shouldSkipCourtMerge(prevRow, fresh) {
  if (!fresh || !prevRow) return false;
  if (fresh.version < prevRow.version) return true;
  if (fresh.version === prevRow.version && sameCourtLineup(prevRow, fresh)) return true;
  return false;
}

/**
 * True when any roster-relevant slice of session state has changed between
 * `prev` and `current`. Drives the debounced-save useLayoutEffect — only
 * schedules a write when this returns true.
 */
export function sessionStateChanged(prev, current) {
  const waitingListsEqual = (a, b) => {
    if (a === b) return true;
    if (!a || !b) return !a && !b;
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (a[i] !== b[i]) return false;
    }
    return true;
  };

  const rosterSnapshotsEqual = (next, prevSnapshot) => {
    if (next === prevSnapshot) return true;
    if (!next || !prevSnapshot) return !next && !prevSnapshot;
    if (next.length !== prevSnapshot.length) return false;
    for (let i = 0; i < next.length; i++) {
      const c = next[i];
      const p = prevSnapshot[i];
      if (c === p) continue;
      if (!sameCourtLineup(c, p)) return false;
    }
    return true;
  };

  return (
    !rosterSnapshotsEqual(current.courtAssignments, prev.courtAssignments) ||
    !rosterSnapshotsEqual(current.nextAssignments, prev.nextAssignments) ||
    !waitingListsEqual(current.waitingList, prev.waitingList)
  );
}
